// Pure helper functions for the Manage page — no React dependencies

const isDraftItem = (item) =>
  item?.is_draft || item?.request_type === 'draft' || item?.request_type === 'bulk_draft';

const truncateTitle = (title, maxLength = 30) => {
  if (!title) return '';
  return title.length > maxLength ? title.substring(0, maxLength) + '...' : title;
};

const formatListingMode = (mode) => {
  if (!mode?.trim()) return '';
  const capitalized = mode.charAt(0).toUpperCase() + mode.slice(1);
  return `${capitalized} Listing`;
};

const getListingMode = (item) => {
  if (!item) return '';
  try {
    const requestTypeMap = {
      'bulk_gen_new': 'New Listing',
      'bulk_gen_existing': 'Existing Listing'
    };
    if (item.request_type && requestTypeMap[item.request_type]) {
      return requestTypeMap[item.request_type];
    }
    const locations = [
      item.listing_mode,
      item.product?.listing_mode,
      item.claude_output?.listing_mode,
      item.output?.listing_mode
    ];
    for (const mode of locations) {
      const formatted = formatListingMode(mode);
      if (formatted) return formatted;
    }
    return '';
  } catch {
    return '';
  }
};

const getCardTitle = (item) => {
  if (!item) return 'Untitled Generation';
  try {
    if (isDraftItem(item)) {
      return truncateTitle(item.draft_name || 'Untitled Draft', 30);
    }
    const projectName =
      item.product?.project_name?.trim() ||
      item.project_name?.trim() ||
      item.claude_output?.project_name?.trim() ||
      item.output?.project_name?.trim();
    if (projectName) return truncateTitle(projectName, 30);
    const output = item.claude_output || item.output || {};
    const fallbackTitle =
      item.updated_content?.title ||
      (Array.isArray(output) ? output[0]?.title : output.title);
    if (fallbackTitle) return truncateTitle(fallbackTitle, 30);
    const product = item.product || {};
    if (product.description) {
      return truncateTitle(`[No Project Name] ${product.description.substring(0, 40)}...`, 30);
    }
    const brandModel = [product.brand_name, product.model_number]
      .filter(Boolean)
      .join(' ')
      .trim();
    return truncateTitle(brandModel ? `[No Project Name] ${brandModel}` : 'Untitled Generation', 30);
  } catch {
    return 'Error getting title';
  }
};

const getBulkProjectName = (bulkProject) => {
  if (isDraftItem(bulkProject)) {
    return truncateTitle(bulkProject.draft_name || 'Untitled Draft', 30);
  }
  if (bulkProject.project_name && bulkProject.project_name.trim()) {
    return truncateTitle(bulkProject.project_name, 30);
  }
  return 'Untitled';
};

const getFirstImage = (images) => images?.length > 0 ? images[0] : null;

const getCardImage = (item) => {
  const output = item.claude_output || item.output || {};
  if (Array.isArray(output) && output.length > 0) {
    const firstOutput = output[0];
    return getFirstImage(firstOutput.generated_images) ||
           getFirstImage(firstOutput.infographic_urls);
  }
  return getFirstImage(output.generated_images) ||
         getFirstImage(output.infographic_urls) ||
         null;
};

const getPreviewImageFromSections = (sections) => {
  if (!sections) return null;
  return sections.primary?.main ||
         (sections.feature && Object.values(sections.feature)[0]) ||
         (sections.application && Object.values(sections.application)[0]) ||
         null;
};

const getBulkPreviewImage = (bulkProject) => {
  if (bulkProject?.preview_image) return bulkProject.preview_image;
  const firstSku = bulkProject?.image_urls?.parent_skus?.[0];
  return firstSku ? getPreviewImageFromSections(firstSku.sections) : null;
};

const transformGenerationData = (generation) => {
  const output = generation.claude_output || generation.output || {};
  const aplusGeneratedImages = output.aplus_generated_images || [];

  const aplusTemplates = aplusGeneratedImages.map((item, index) => ({
    image_url: {
      url: item.url || item.image_url || item,
      page: item.page || index + 1
    },
    page: item.page || index + 1,
    ...item
  }));

  let childProducts = generation.child_products || [];
  if (output.child_output && output.child_output.children) {
    const childrenObj = output.child_output.children;
    childProducts = Object.keys(childrenObj).map(key => childrenObj[key]);
  }

  return {
    product: generation.product,
    claude_output: output,
    child_products: childProducts,
    inserted_id: generation._id || generation.id,
    editor_folder_id: generation.editor_folder_id || generation._id || generation.id,
    listing_mode: generation.listing_mode,
    aplus_templates: aplusTemplates
  };
};

const formatDate = (dateString) => {
  if (!dateString) return 'Unknown Date';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return 'Invalid Date';
  }
};

export {
  isDraftItem,
  getListingMode,
  getCardTitle,
  getBulkProjectName,
  getCardImage,
  getBulkPreviewImage,
  transformGenerationData,
  formatDate
};
